import {
  ChangeEvent,
  FC,
  FocusEventHandler,
  FocusEvent,
  useState,
  useEffect,
  useRef,
} from "react";
import ChevronDownIcon from "@heroicons/react/outline/ChevronDownIcon";
import CheckIcon from "@heroicons/react/solid/CheckIcon";
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  format,
  AsYouType,
  validatePhoneNumberLength,
} from "libphonenumber-js";
import { Transition } from "@headlessui/react";
import { useFormState } from "react-hook-form";
import { useTranslation } from "next-i18next";

import TextInput, { TextInputProps } from "./text-input";
import ErrorMessageComponent from "./error-message-component";

import { classNames } from "@utils/class-names";

type Props = Omit<TextInputProps, "format">;

const countries = getCountries().map((country) => ({
  country,
  code: getCountryCallingCode(country),
}));

function formatPhoneNumber(country: CountryCode, phone: string): string {
  return format(`+${getCountryCallingCode(country)}${phone}`, "E.164");
}

function getPhoneInput(country: CountryCode, phone = ""): string {
  return new AsYouType(country).input(phone);
}

export function getPhoneAndCountry(input = ""): {
  phonePart: string;
  countryCode: CountryCode;
} {
  const ayt = new AsYouType();
  ayt.input(input);
  const countryCode = ayt.country || "ME";
  const nationalNumber = ayt.getNationalNumber();
  const phonePart = getPhoneInput(
    countryCode,
    `${
      nationalNumber && !nationalNumber.startsWith("0") ? "0" : ""
    }${nationalNumber}`
  );

  return { phonePart, countryCode };
}

const PhoneNumberInput: FC<Props> = (props) => {
  const { phonePart, countryCode } = getPhoneAndCountry(props.value);
  const [phone, setPhone] = useState(phonePart);
  const [country, setCountry] = useState(countryCode);
  const [message, setMessage] = useState("");
  const [hasErrors, setHasErrors] = useState(false);
  const { isValid } = useFormState();
  const [code, setCode] = useState<string>(
    getCountryCallingCode(country).toString()
  );
  const [focus, setFocus] = useState(false);
  const [activeCountry, setActiveCountry] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("forms", { keyPrefix: "validation.phone" });

  const filteredCountries = countries.filter(({ code: c }) =>
    c.startsWith(code)
  );

  useEffect(() => {
    const { phonePart, countryCode } = getPhoneAndCountry(props.value);
    setCountry(countryCode);
    setPhone(phonePart);
  }, [props.value]);

  useEffect(() => {
    const errors = validatePhoneNumberLength(
      formatPhoneNumber(country, phone),
      country
    );

    setHasErrors(!isValid && !!errors);

    if (hasErrors) {
      switch (errors) {
        case "INVALID_COUNTRY":
          setMessage(t("country"));
          break;
        case "INVALID_LENGTH":
          setMessage(t("length"));
          break;
        case "NOT_A_NUMBER":
          if (phone.length > 1) {
            setMessage(t("character"));
          }
          break;
        case "TOO_SHORT":
          setMessage(t("min"));
          break;
        case "TOO_LONG":
          setMessage(t("max"));
          break;
        default:
          setMessage("");
          break;
      }
    }
  }, [phone, country, isValid, hasErrors, t]);

  useEffect(() => {
    if (activeCountry !== -1) {
      const countryElement = document.querySelector(
        `li#${filteredCountries[activeCountry].country}`
      );

      if (countryElement) {
        countryElement.scrollIntoView({ behavior: "auto", block: "nearest" });
      }
    }
  }, [activeCountry, filteredCountries]);

  function callProps(country: CountryCode, phone: string): void {
    const value = formatPhoneNumber(country, phone);

    if (props.onChange) {
      props.onChange({
        target: { value },
      } as ChangeEvent<HTMLInputElement>);
    }

    if (props.setValue) {
      props.setValue(value);
    }
  }

  const handleBlur: FocusEventHandler<HTMLDivElement | HTMLInputElement> =
    () => {
      if (props.onBlur) {
        props.onBlur({
          target: { value: formatPhoneNumber(country, phone) },
        } as FocusEvent<HTMLInputElement>);
      }
    };

  const handlePhoneChange = (phoneNumber: string) => {
    const value = getPhoneInput(country, phoneNumber);

    if (validatePhoneNumberLength(value, country) !== "TOO_LONG") {
      setPhone(value);

      callProps(country, value);
    }
  };

  const handleCountryChange = (value: CountryCode) => {
    setCountry(value);
    const phoneValue = getPhoneInput(value, phone);

    setPhone(phoneValue);

    callProps(value, phoneValue);
  };

  return (
    <div>
      <div className="flex space-x-2">
        <div
          className="relative w-5/12"
          onKeyDown={({ code }) => {
            if (
              code === "ArrowDown" &&
              activeCountry !== filteredCountries.length - 1
            ) {
              setActiveCountry(activeCountry + 1);
            } else if (code === "ArrowUp" && activeCountry > 0) {
              setActiveCountry(activeCountry - 1);
            } else if (code === "Enter" && activeCountry !== -1) {
              handleCountryChange(filteredCountries[activeCountry].country);
              inputRef.current?.blur();
            }
          }}
        >
          <div>Kod*</div>
          <div className="w-full h-10 rounded border border-black border-l-2 text-left p-2 flex justify-between items-center bg-white">
            <input
              type="text"
              className="w-full outline-none border-none h-full focus:ring-0 p-0"
              value={
                focus ? code : `${country}-(+${getCountryCallingCode(country)})`
              }
              ref={inputRef}
              onFocus={() => setFocus(true)}
              onBlur={() => setFocus(false)}
              onKeyDown={(event) => {
                if (["ArrowDown", "ArrowUp"].includes(event.code)) {
                  event.preventDefault();
                }
              }}
              onChange={(event) => {
                event.preventDefault();

                const newValue = event.target.value
                  .replace(/\D/g, "")
                  .slice(0, 3);

                setCode(newValue);
              }}
            />
            <ChevronDownIcon
              className={classNames(
                "w-4 transition duration-200",
                focus ? "-rotate-180" : ""
              )}
            />
          </div>
          <Transition
            show={focus}
            unmount={false}
            enter="transition duration-200 ease-out origin-top"
            enterFrom="scale-0 opacity-0"
            enterTo="scale-100 opacity-100"
            leave="transition duration-200 ease-in origin-top"
            leaveFrom="scale-100 opacity-100"
            leaveTo="scale-0 opacity-0"
          >
            <div className="absolute bg-white w-full rounded border border-black divide-y divide-black max-h-40 overflow-y-auto">
              {filteredCountries.map((item, index) => (
                <li
                  className={classNames(
                    "cursor-pointer bg-white p-2 align-middle flex justify-between items-center transition duration-200 focus:bg-gray-300 active:bg-gray-300",
                    index === activeCountry ? "bg-gray-300" : ""
                  )}
                  key={item.country}
                  id={item.country}
                  onClick={() => {
                    handleCountryChange(item.country);
                  }}
                >
                  {item.country}-(+{item.code})
                  {country === item.country && <CheckIcon className="w-4" />}
                </li>
              ))}
            </div>
          </Transition>
        </div>
        <div className="w-7/12">
          <TextInput
            {...{
              name: `${props.name}-national`,
              placeholder: props.placeholder,
              autoComplete: props.autoComplete,
              label: props.label,
              required: props.required,
              showLabel: props.showLabel,
              value: phone,
              onBlur: handleBlur,
              setValue: handlePhoneChange,
            }}
          />
        </div>
      </div>
      <ErrorMessageComponent {...{ errors: hasErrors ? message : undefined }} />
    </div>
  );
};

export default PhoneNumberInput;
